<template>
  <tr
    class="group border-b border-grey-300 leading-tight hover:bg-primary-50 cursor-pointer transition-all transition-normal"
    @click="openModal"
  >
    <th class="text-left">
      <div class="flex items-center">
        <div class="pl-4">
          <icon
            v-if="isConfirmed"
            :icon="approved ? 'check' : 'important'"
            class="w-6 h-6"
            :class="approved ? 'icons-positive' : 'icons-negative'"
          />
          <div
            v-else
            class="w-6 h-6 rounded-full border-2 flex items-center justify-center font-bold text-xs leading-none text-grey-500 group-hover:text-primary-600 group-hover:border-primary-600 transition-all transition-normal"
          >
            {{ position }}
          </div>
        </div>

        <div class="px-4 py-2">
          <span class="flex items-center">
            <span
              v-if="assessment.stream && !isSingleStream"
              class="border rounded px-2 py-px align-center text-xs inline-block leading-none mr-2"
              :style="getStreamColorsByKey(assessment.stream)"
            >
              {{ getStreamNameByKey(assessment.stream) }}
            </span>
            {{ assessment.company.name }}
          </span>
          <small class="font-normal truncate">{{ assessment.ref }}</small>
        </div>
      </div>
    </th>
    <td class="px-4 py-2 text-center font-bold">
      {{ fullyAssessedCount }} / {{ numberOfAssessors }}
    </td>
    <td class="text-center">
      <div
        class="p-4 text-3xl leading-none text-white"
        :class="gradingBgColorScore(totalPercentage)"
      >
        <b>{{ totalPercentage }}<small>%</small></b>
        <small
          v-if="isContinuationPhase"
          class="text-xs block font-bold mt-1"
        >
          {{ totalPercentageDifference }}
        </small>
      </div>
    </td>
    <td v-if="showTrlMrl" class="text-center">
      {{ totalPassTrlMrl }} / {{ numberOfAssessors }}
      <span v-if="totalPassTrlMrl === numberOfAssessors" class="inline-flex bg-positive-500 text-positive-50 font-bold w-6 h-6 rounded-full flex items-center justify-center">&check;</span>
      <span v-else-if="totalReviewTrlMrl" class="inline-flex bg-neutral-500 text-neutral-50 font-bold w-6 h-6 rounded-full flex items-center justify-center">&excl;</span>
      <span v-else-if="totalFailedTrlMrl === numberOfAssessors" class="inline-flex bg-negative-500 text-negative-50 font-bold w-6 h-6 rounded-full flex items-center justify-center">&times;</span>
    </td>
    <td v-if="booster" class="text-center">
      <div
        class="p-4 text-3xl leading-none text-white"
        :class="gradingBgColorScore(totalBoosterPercentage)"
      >
        <b>{{ totalBoosterPercentage }}<small>%</small></b>
      </div>
    </td>
    <td v-if="showRecommended" class="px-4 py-2 text-center font-bold">
      {{ totalRecommended }} / {{ numberOfAssessors }}
    </td>
    <td class="px-4 py-2 text-center">
      {{ minRangeScore }}
    </td>
    <td class="px-4 py-2 text-center">
      {{ maxRangeScore }}
    </td>
    <td class="px-4 py-2 text-center">
      {{ rangeScore }}
    </td>
  </tr>
</template>
<script>
import { sum, min, max } from "lodash";
import { gradingColourMixin } from "../../mixins/gradingColour";
import { competitionStreamsMixin } from "../../mixins/competitionStreams";

export default {
  name: "LeagueTableEntry",
  mixins: [gradingColourMixin, competitionStreamsMixin],
  props: {
    assessment: {
      type: Object,
      required: true
    },
    position: {
      type: Number,
      required: true
    },
    showRecommended: {
      type: Boolean,
      required: false
    },
    showTrlMrl: {
      type: Boolean,
      required: false
    },
    booster: {
      type: [Object, null],
      required: false,
      default: null
    }
  },
  computed: {
    isContinuationPhase() {
      return !!this.$page.marking_phase.continuation_of_marking_phase_id;
    },

    approved() {
      return this.assessment.pivot.approved_at;
    },
    rejected() {
      return this.assessment.pivot.rejected_at;
    },
    isConfirmed() {
      return this.assessment.pivot.confirmed_at;
    },

    application() {
      return this.assessment.application || [];
    },

    numberOfAssessors() {
      return this.assessment.assessors.length;
    },

    fullyAssessedCount() {
      return this.assessment.all_assessor_grades_current_phase.filter(
        ag => ag.grading_completed
      ).length;
    },

    fullyAssessed() {
      return this.numberOfAssessors === this.fullyAssessedCount;
    },

    totalRecommended() {
      return this.assessment.all_assessor_grades_current_phase.filter(
        ag => ag.recommended
      ).length;
    },

    totalPassTrlMrl() {
      return this.assessment.all_assessor_grades_current_phase.filter(ass => ass.scores.filter(
        ag => ag.key === 'trl_mrl' && ag.grade === 1
      ).length).length;
    },

    totalReviewTrlMrl() {
      return this.assessment.all_assessor_grades_current_phase.filter(ass => ass.scores.filter(
        ag => ag.key === 'trl_mrl' && ag.grade === 3
      ).length).length;
    },

    totalFailedTrlMrl() {
      return this.assessment.all_assessor_grades_current_phase.filter(ass => ass.scores.filter(
        ag => ag.key === 'trl_mrl' && ag.grade === 0
      ).length).length;
    },

    totalScore() {
      return sum(this.allAssessorTotals);
    },

    totalBoosterScore() {
      return sum(this.allAssessorBoosterScores);
    },

    totalPercentage() {
      if(!this.maxScore) return 0;
      return parseFloat(((this.totalScore / this.maxScore) * 100).toFixed(2));
    },

    totalBoosterPercentage() {
      if(!this.maxBoosterScore) return 0;
      return parseFloat(((this.totalBoosterScore / this.maxBoosterScore) * 100).toFixed(2));
    },

    previousTotalPercentage() {
      return parseFloat(
        (
          (this.assessment.previousTotalScore / this.previousMaxScore) *
          100
        ).toFixed(2)
      );
    },

    totalPercentageDifference() {
      const diff = (this.totalPercentage - this.previousTotalPercentage).toFixed(2);
      return `(${diff > 0 ? "+" + diff : diff}%)`;
    },

    maxScore() {
      return (
        this.$page.marking_phase.template?.max_score * this.numberOfAssessors ??
        0
      );
    },

    maxBoosterScore() {
      if (!this.booster) return 0;
      return (
        this.booster?.max_score * this.numberOfAssessors ?? 0
      );
    },

    previousMaxScore() {
      if (!this.assessment.all_assessor_grades_previous_phase) return 0;
      return (
        this.$page.marking_phase.template?.max_score *
          this.assessment.all_assessor_grades_previous_phase.length ?? 0
      );
    },

    average() {
      return 0;
    },

    validateBoosterQuestionScores() {
      if (!this.booster) return false;
      return this.assessment.all_assessor_grades_current_phase.some(assessorGrade => {
        let scores = assessorGrade.scores.filter(score => this.booster.questions.includes(score.key)).map(score => score.grade ?? 0);
        return sum(scores) > 0;
      });
    },

    allAssessorBoosterScores() {
      if(!this.validateBoosterQuestionScores) return [];
      return this.assessment.all_assessor_grades_current_phase.reduce(
        (arr, assessorGrade) => {
          let scores = assessorGrade.scores.filter(score => this.booster.scores.includes(score.key)).map(score => score.grade ?? 0);
          arr.push(...scores);
          return arr;
        },
        []
      );
    },

    allAssessorAverages() {
      return this.assessment.all_assessor_grades_current_phase.reduce(
        (arr, assessorGrade) => {
          arr.push(assessorGrade.average);
          return arr;
        },
        []
      );
    },

    allAssessorTotals() {
      return this.assessment.all_assessor_grades_current_phase.reduce(
        (arr, assessorGrade) => {
          arr.push(assessorGrade.total);
          return arr;
        },
        []
      );
    },

    minRangeScore() {
      const min = this.min(this.allAssessorTotals);
      return isNaN(min) ? "-" : min;
    },
    maxRangeScore() {
      const max = this.max(this.allAssessorTotals);
      return isNaN(max) ? "-" : max;
    },

    rangeScore() {
      const range = this.maxRangeScore - this.minRangeScore;
      return isNaN(range) ? "-" : range;
    },
  },
  watch: {
    assessment: {
      handler() {
        this.$el.classList.add("bg-primary-100");
        setTimeout(() => this.$el.classList.remove("bg-primary-100"), 1000);
      },
      deep: true
    }
  },
  methods: {
    gradingBgColorScore(score) {
      if(!score) score = 0;
      return {
        0: "bg-grading-value-1",
        10: "bg-grading-value-2",
        20: "bg-grading-value-3",
        30: "bg-grading-value-4",
        40: "bg-grading-value-5",
        50: "bg-grading-value-6",
        60: "bg-grading-value-7",
        70: "bg-grading-value-8",
        80: "bg-grading-value-9",
        90: "bg-grading-value-10",
        100: "bg-grading-value-10"
      }[Math.floor(score / 10) * 10];
    },

    gradingTextColorScore(score) {
      if(!score) score = 0;
      return {
        0: "text-grading-value-1",
        10: "text-grading-value-2",
        20: "text-grading-value-3",
        30: "text-grading-value-4",
        40: "text-grading-value-5",
        50: "text-grading-value-6",
        60: "text-grading-value-7",
        70: "text-grading-value-8",
        80: "text-grading-value-9",
        90: "text-grading-value-10",
        100: "text-grading-value-10"
      }[Math.floor(score / 10) * 10];
    },

    gradingBorderColorScore(score) {
      if(!score) score = 0;
      return {
        0: "border-grading-value-1",
        10: "border-grading-value-2",
        20: "border-grading-value-3",
        30: "border-grading-value-4",
        40: "border-grading-value-5",
        50: "border-grading-value-6",
        60: "border-grading-value-7",
        70: "border-grading-value-8",
        80: "border-grading-value-9",
        90: "border-grading-value-10",
        100: "border-grading-value-10"
      }[Math.floor(score / 10) * 10];
    },

    min,
    max,

    openModal() {
      this.$emit("entry:details", this.assessment.uuid);
    }
  }
};
</script>
